import React, { useCallback, useEffect, useRef } from "react";

import CustomDropDown from "input/CustomDropDown";
import FilterDropDown from "./FilterDropDown";
import OptionDropDown from "./OptionDropDown";
import { ActivityGuideLink } from "utility/ActivityGuides";
import { ViewState } from "./EditCoachingPlanHead";
import { getValueAtPath } from "utility/ObjectUtils";
import { generateRandomString } from "utility/Utils";
import { parseSemverString, satisfiesMinVersion } from "utility/SemverUtils";
import { scoreBadgeBackgroundColor, scoreBarColor, scoreEncouragement, scoreTextColor } from "./ScoreUtils";
import { useDebugState } from "utility/StateDebugger";

enum AssignmentSaveState {
    Assigned = 0,
    Unsaved = 1,
    Duplicate = 2
}

/**
 * Renders the header of the assignment, including the title and status badges.
 */

interface AssignmentHeaderProps {
    assignmentDurationSaveState: any;
    assignmentSaveState: any;
    assignmentSettingsSaveState: any;
    isEditable: boolean;
    title: any;
    // Callbacks
    onReset: any;
}

const AssignmentHeader = ({
    assignmentDurationSaveState,
    assignmentSaveState,
    assignmentSettingsSaveState,
    isEditable,
    title,
    // Callbacks
    onReset
}: AssignmentHeaderProps) => {
    const isUnsaved = isEditable && (assignmentSaveState.includes(AssignmentSaveState.Unsaved) || assignmentDurationSaveState.includes(AssignmentSaveState.Unsaved) || assignmentSettingsSaveState.includes(AssignmentSaveState.Unsaved));
    const isDuplicate = isEditable && (assignmentSaveState.includes(AssignmentSaveState.Duplicate) || assignmentDurationSaveState.includes(AssignmentSaveState.Duplicate));
    const isAssigned = isEditable && (!isUnsaved && !isDuplicate);

    const renderSaveStatePills = () => {
        if (!isAssigned && !isUnsaved && !isDuplicate) {
            return null;
        }

        return (
            <React.Fragment>
                {
                    (isAssigned) && (
                        <div className="h-[22px] mix-blend-multiply justify-start items-start inline-flex">
                            <div className="px-3.5 py-0.5 bg-brand-50 rounded-2xl justify-center items-center gap-1 flex">
                                <img className="w-3 h-3 relative" src="/icons/check-square.svg" alt="assigned-icon"/>
                                <div className="text-center text-brand-700 text-xs font-medium leading-[18px]">Assigned</div>
                            </div>
                        </div>
                    )
                }
                {
                    (isUnsaved) && (
                        <div className="h-[22px] mix-blend-multiply justify-start items-center inline-flex">
                            <div className="px-3.5 py-0.5 bg-yellow-50 rounded-2xl justify-center items-center gap-1 flex">
                                <img className="w-3 h-3 relative" src="/icons/edit-02.svg" alt="edit-icon"/>
                                <div className="text-center text-yellow-700 text-xs font-medium leading-[18px]">Unsaved</div>
                            </div>
                        </div>
                    )
                }
                {
                    (isDuplicate) && (
                        <div className="h-[22px] mix-blend-multiply justify-start items-start inline-flex">
                            <div className="px-3.5 py-0.5 bg-red-50 rounded-2xl justify-center items-center gap-1 flex">
                                <img className="w-3 h-3 relative" src="/icons/copy-03.svg" alt="copy-icon"/>
                                <div className="text-center text-red-700 text-xs font-medium leading-[18px]">Duplicate</div>
                            </div>
                        </div>
                    )
                }
            </React.Fragment>
        );
    };

    return (
        <div className="justify-start items-center inline-flex w-full gap-x-2">
            <div className="text-gray-900 text-sm font-semibold text-nowrap leading-tight">{title}</div>
            {isEditable && renderSaveStatePills()}
            <div className="w-full" />
            {
                isEditable && (
                    (isUnsaved)
                        ? (
                            <React.Fragment>
                                <div className="w-full"/>
                                <button
                                    className="h-7 px-0.5 hover:bg-gray-300 bg-white rounded-lg shadow border border-gray-300 justify-center items-center flex relative cursor-pointer"
                                    onClick={onReset}
                                >
                                    <img className="w-16 h-16" src="/icons/flip-backward.svg" alt="revert-icon"/>
                                </button>
                            </React.Fragment>
                        )
                        : (
                            <React.Fragment>
                                <div className="w-full"/>
                                <div className="h-7 justify-center items-center flex relative"></div>
                            </React.Fragment>
                        )
                )
            }
        </div>
    );
};

/**
 * Renders the interactive controls for an assignment, such as dropdowns and filters.
 */

interface AssignmentControlsProps {
    activity: any;
    assignmentDuration: any;
    assignmentDurationSaveState: any;
    assignmentSaveState: any;
    assignmentSettings: any;
    assignmentSettingsSaveState: any;
    coachingFilter: any;
    debugState: any;
    doneFilter: any;
    durationOptions: any;
    filteredActivityOptions: any;
    getActivityTypeIcon: any;
    getBorderColor: any;
    index: number;
    isEditable: boolean;
    isRoutineActivity: any;
    meditationFilter: any;
    movementFilter: any;
    // Callbacks
    changeActivityCallback: any;
    changeActivityDurationCallback: any;
    selectedActivityId: any;
    setCoachingFilter: any;
    setDoneFilter: any;
    setMeditationFilter: any;
    setMovementFilter: any;
}

const AssignmentControls = ({
    activity,
    assignmentDuration,
    assignmentDurationSaveState,
    assignmentSaveState,
    assignmentSettings,
    assignmentSettingsSaveState,
    coachingFilter,
    debugState,
    doneFilter,
    durationOptions,
    filteredActivityOptions,
    getActivityTypeIcon,
    getBorderColor,
    index,
    isEditable,
    isRoutineActivity,
    meditationFilter,
    movementFilter,
    // Callbacks
    changeActivityCallback,
    changeActivityDurationCallback,
    selectedActivityId,
    setCoachingFilter,
    setDoneFilter,
    setMeditationFilter,
    setMovementFilter
}: AssignmentControlsProps) => {
    const filterOptions = isRoutineActivity
        ? [
            { label: "Exclude Completed Activities", value: doneFilter, onChange: setDoneFilter }
        ]
        : [
            { label: "Coaching Activities", value: coachingFilter, onChange: setCoachingFilter },
            { label: "Meditation Activities", value: meditationFilter, onChange: setMeditationFilter },
            { label: "Movement Activities", value: movementFilter, onChange: setMovementFilter },
            { label: "Exclude Completed Activities", value: doneFilter, onChange: setDoneFilter }
        ];

    return (
        <div className="inline-flex self-stretch justify-start items-center gap-2.5 h-9">
            {getActivityTypeIcon(activity?.type) ?? null}
            <div className="w-fit">
                {
                    filteredActivityOptions && (
                        <CustomDropDown
                            buttonBorderColor={getBorderColor(assignmentSaveState)}
                            defaultValue={selectedActivityId}
                            displayDisabledWhenLocked={false}
                            locked={!isEditable}
                            optionPillBackgroundColors={["bg-brand-50", "bg-gray-50", "bg-warning-50"]}
                            optionPillTextColors={["text-brand-700", "text-gray-700", "text-warning-700"]}
                            options={filteredActivityOptions}
                            optionsAlignRight={false}
                            // Callbacks
                            onSelectionCallback={changeActivityCallback}
                            // Debugging
                            debugState={debugState}
                        />
                    )
                }
            </div>
            {
                (isEditable && activity) && (
                    <ActivityGuideLink activityId={selectedActivityId} />
                )
            }
            {
                (isEditable && activity && assignmentSettings) && (
                    <OptionDropDown options={assignmentSettings} borderState={getBorderColor(assignmentSettingsSaveState)} index={index}/>
                )
            }
            <div className="w-full">
                {
                    (isEditable) && (
                        <FilterDropDown options={filterOptions} />
                    )
                }
            </div>
            <div className="grow shrink self-stretch" />
            <div className="w-fit">
                {
                    (isEditable && activity && durationOptions && filteredActivityOptions?.length > 0) && (
                        <CustomDropDown
                            buttonBorderColor={getBorderColor(assignmentDurationSaveState)}
                            defaultValue={assignmentDuration}
                            options={durationOptions}
                            // Callbacks
                            onSelectionCallback={changeActivityDurationCallback}
                            // Debugging
                            debugState={false}
                        />
                    )
                }
            </div>
        </div>
    );
};

/**
 * Renders the statistics for an activity, like the best score and completions.
 */

interface ActivityStatsProps {
    activity: any;
    bestWeeklyScore: any;
    contextBadgeBackgroundColor: any;
    contextBarColor: any;
    contextLevel: any;
    contextTextColor: any;
    getActivityBadge: any;
    totalWeeklyCompletions: any;
}

const ActivityStats = ({
    activity,
    bestWeeklyScore,
    contextBadgeBackgroundColor,
    contextBarColor,
    contextLevel,
    contextTextColor,
    getActivityBadge,
    totalWeeklyCompletions
}: ActivityStatsProps) => {
    if (!activity) {
        return null;
    }

    return (
        <React.Fragment>
            {
                activity.type === "Movement" && (
                    <div className="self-stretch justify-start items-center gap-2 inline-flex">
                        <img className="w-5 h-5 relative" src="/icons/score.svg" alt="score-icon"/>
                        <div className="text-sm font-medium leading-tight text-nowrap text-slate-700">Best Score</div>
                        <div className="flex w-full h-2 relative rounded-lg bg-gray-200">
                            <div
                                className={`h-2 left-0 top-0 absolute rounded-full ${contextBarColor(bestWeeklyScore)}`}
                                style={{ width: `${Math.min(100, Math.round((bestWeeklyScore / 5000) * 100))}%` }}
                            />
                        </div>
                        <div className="text-right text-sm font-medium leading-tight text-slate-700 ">{bestWeeklyScore}</div>
                        <div className="mix-blend-multiply justify-start items-start flex">
                            <div className={`px-2 py-0.5 rounded-2xl justify-center items-center flex ${contextBadgeBackgroundColor(bestWeeklyScore)}`}>
                                <div className={`text-center text-nowrap text-xs font-medium leading-[18px] ${contextTextColor(bestWeeklyScore)}`}>{contextLevel(bestWeeklyScore)}</div>
                            </div>
                        </div>
                    </div>
                )
            }
            <div className="self-stretch items-center gap-2 leading-tight relative inline-flex">
                <div className="h-5 gap-2 justify-start items-start flex">
                    <img className="w-5 h-5 relative flex-col justify-start items-start flex" src="/icons/greyCheck.svg" alt="check-icon"/>
                    <div className="text-slate-700 text-sm font-medium text-nowrap">Completions</div>
                </div>
                <div className="self-stretch justify-end absolute items-center gap-2 inline-flex right-0">
                    <img className="w-7 h-7" src={getActivityBadge(activity.type, totalWeeklyCompletions)} alt="badge-icon"/>
                    <div className="text-right text-slate-700 text-sm font-medium leading-tight">{totalWeeklyCompletions}</div>
                </div>
            </div>
        </React.Fragment>
    );
};

interface AssignmentEditorProps {
    activities: any;
    completedRoutineData: any;
    currentAssignments: any;
    defaultAssignment: any;
    defaultDuration: any;
    index: any;
    isCalibrationActivity: boolean;
    isRoutineActivity: boolean;
    originalAssignment: any;
    originalDuration: any;
    refreshTrigger: any;
    state: ViewState;
    vetKitVersion: string;
    week: any;
    // Callbacks
    refreshParentCallback: () => void;
    setActivityCallback: (activityId: string | null, week: any, index: any) => void;
    setDurationCallback: (duration: number, activityId: string, week: any, index: any) => void;
    setOptionsCallback: (label: string, value: any, week: any, index: any) => void;
    // Debugging
    debugState?: boolean;
}

const AssignmentEditor = ({
    activities,
    completedRoutineData,
    currentAssignments,
    defaultAssignment,
    defaultDuration,
    index,
    isCalibrationActivity,
    isRoutineActivity,
    originalAssignment,
    originalDuration,
    refreshTrigger,
    state,
    vetKitVersion,
    week,
    // Callbacks
    refreshParentCallback,
    setActivityCallback,
    setDurationCallback,
    setOptionsCallback,
    // Debugging
    debugState = false
}: AssignmentEditorProps) => {

    const debugKeyRef = useRef(`ActivityAssignmentPanel-${generateRandomString(6)}`);
    const debugKey = debugKeyRef.current;

    const [activityOptions, setActivityOptions] = useDebugState<any[] | null>(null, "activityOptions", debugKey, debugState);
    const [assignmentDuration, setAssignmentDuration] = useDebugState(defaultDuration ? defaultDuration / 60 : 0, "assignmentDuration", debugKey, debugState);
    const [assignmentDurationSaveState, setAssignmentDurationSaveState] = useDebugState([AssignmentSaveState.Assigned], "assignmentDurationSaveState", debugKey, debugState);
    const [assignmentSaveState, setAssignmentSaveState] = useDebugState([AssignmentSaveState.Assigned], "assignmentSaveState", debugKey, debugState);
    const [assignmentSettings, setAssignmentSettings] = useDebugState<any[] | null>(null, "assignmentSettings", debugKey, debugState);
    const [assignmentSettingsSaveState, setAssignmentSettingsSaveState] = useDebugState([AssignmentSaveState.Assigned], "assignmentSettingsSaveState", debugKey, debugState);
    const [coachingFilter, setCoachingFilter] = useDebugState(false, "coachingFilter", debugKey, debugState);
    const [doneFilter, setDoneFilter] = useDebugState(false, "doneFilter", debugKey, debugState);
    const [durationOptions, setDurationOptions] = useDebugState<any[] | null>(null, "durationOptions", debugKey, debugState);
    const [filteredActivityOptions, setFilteredActivityOptions] = useDebugState<any[] | null>(null, "filteredActivityOptions", debugKey, debugState);
    const [isInitialized, setIsInitialized] = useDebugState(false, "isInitialized", debugKey, debugState);
    const [meditationFilter, setMeditationFilter] = useDebugState(false, "meditationFilter", debugKey, debugState);
    const [movementFilter, setMovementFilter] = useDebugState(false, "movementFilter", debugKey, debugState);
    const [optionChanged, setOptionChanged] = useDebugState(null, "optionChanged", debugKey, debugState);
    const [selectedActivityId, setSelectedActivityId] = useDebugState(null, "selectedActivityId", debugKey, debugState);

    const myRenderCount = useRef(0);
    myRenderCount.current += 1;
    if (debugState) {
        console.log(`${debugKey} render[${myRenderCount.current}]: selectedActivityId[${selectedActivityId ? selectedActivityId : "no selection"}]`);
    }

    const noActivityId = "No Assignment";

    const noActivity = {
        img: `/icons/badges/ActivityIcon_NoAssignment.png`,
        label: "No Assignment",
        value: noActivityId
    };

    // ---=== Utilities ===--- //

    const isActivitySelected = () => {
        return selectedActivityId && selectedActivityId !== noActivity.value;
    }

    const getDefaultActivityId = () => {
        if (defaultAssignment) {
            return defaultAssignment.Id;
        }
        if (isRoutineActivity) {
            // This could be an issue if a routine activity truly has no default.
            // For now, let's assume one is always passed for routines.
            return null;
        }
        return noActivity.value;
    }

    const getOriginalActivityId = () => {
        if (originalAssignment !== null) {
            return originalAssignment.Id;
        }
        if (isRoutineActivity) {
            return null;
        }
        return noActivityId;
    }

    const compareSettings = (newSettings, previousSettingsHash: string) => {
        if (originalAssignment && activities[originalAssignment.Id].hasOptions) {
            return (previousSettingsHash === JSON.stringify(newSettings));
        }
        return false;
    };

    const changeOption = (label: string, value, options) => {
        let settings = options;
        let previousOptionsHash: string;

        if (originalAssignment && selectedActivityId == originalAssignment.Id) {
            let previousOptions = [];
            activities[originalAssignment.Id].optionLabels.forEach(
                (label: string) => {
                    const value: boolean = originalAssignment.Settings?.[label] ?? false;
                    previousOptions.push({
                        label: label,
                        value: value,
                        onChange: setOptionChanged
                    });
                }
            );
            previousOptionsHash = JSON.stringify(previousOptions);
        }
        else {
            previousOptionsHash = "";
        }

        settings?.forEach(
            setting => {
                if (setting.label === label) {
                    setting.value = value;
                }
            }
        );

        setAssignmentSettingsSaveState(compareSettings(settings, previousOptionsHash) ? [AssignmentSaveState.Assigned] : [AssignmentSaveState.Unsaved]);
        setAssignmentSettings(settings);

        setOptionsCallback(label, value, week, index);
    };

    const resetActivity = () => {
        setAssignmentSaveState([AssignmentSaveState.Assigned]);
        setAssignmentDurationSaveState([AssignmentSaveState.Assigned]);

        let originalActivityId =  getOriginalActivityId();
        setSelectedActivityId(originalActivityId);
        setActivityCallback(originalActivityId === noActivityId ? null : originalActivityId, week, index);

        if (originalAssignment !== null) {
            if (debugState) {
                console.info(`[${debugKey}].resetActivity Clearing activity options (setActOptions)!`);
            }
            setActivityOptions(null);
        }

        if (originalDuration && originalDuration !== 0 && originalAssignment !== null && getValueAtPath(activities[originalAssignment.Id], "Settings.Duration.Value", -1) !== -1) {
            setAssignmentDuration(originalDuration / 60);
            setDurationCallback(originalDuration, originalAssignment.Id, week, index);
        }

        if (originalAssignment && activities[originalAssignment.Id].hasOptions) {
            let options = [];
            activities[originalAssignment.Id].optionLabels.forEach(
                (label: string, index: number) => {
                    const value: boolean = originalAssignment.Settings?.[label] ?? false;
                    options.push({
                        label: label,
                        description: activities[originalAssignment.Id].optionDescriptions[index],
                        value: value,
                        onChange: setOptionChanged
                    });
                    setOptionsCallback(label, value, week, index);
                }
            );
            setAssignmentSettings(options);
        }
        else {
            setAssignmentSettings(null);
        }

        setAssignmentSettingsSaveState([AssignmentSaveState.Assigned]);

        refreshParentCallback();
    };

    // ---=== useCallbacks ===--- //

    const changeActivityCallback = useCallback((activityId: string) => {
        if (activityId === undefined) {
            activityId = selectedActivityId;
        }

        let prevActivityId = selectedActivityId;
        setSelectedActivityId(activityId);

        setActivityCallback(activityId === noActivityId ? null : activityId, week, index);

        if ((originalAssignment && activityId === noActivityId) || (originalAssignment && activityId === originalAssignment.Id) || (activityId === prevActivityId)) {
            setAssignmentSaveState([AssignmentSaveState.Assigned]);
            if (prevActivityId !== activityId && activityId !== noActivityId) {
                if (!assignmentSettings && activities[activityId].hasOptions) {
                    let options = [];
                    activities[originalAssignment.Id].optionLabels.forEach(
                        (label: string, index: number) => {
                            const value: boolean = originalAssignment.Settings?.[label] ?? false;
                            options.push({
                                label: label,
                                description: activities[originalAssignment.Id].optionDescriptions[index],
                                value: value,
                                onChange: setOptionChanged
                            });
                            setOptionsCallback(label, value, week, index);
                        }
                    );
                    setAssignmentSettings(options);
                }
            }
        }
        else {
            setAssignmentSaveState([AssignmentSaveState.Unsaved]);
            if (activityId !== noActivityId && activities[activityId].hasOptions) {
                let options = [];
                activities[activityId].optionLabels.forEach(
                    (label: string, index: number) => {
                        options.push({
                            label: label,
                            description: activities[activityId].optionDescriptions[index],
                            value: false,
                            onChange: setOptionChanged
                        });
                    }
                );
                setAssignmentSettings(options);
            }
            else {
                setAssignmentSettings(null);
            }
        }

        if (activityId !== noActivityId && activityId !== selectedActivityId) {
            setDurationOptions(null);
            if (prevActivityId !== activityId && (originalAssignment ? activityId === originalAssignment.Id : false)) {
                setAssignmentDuration(originalDuration / 60);
                setDurationCallback(originalDuration, activityId, week, index);
            }
            else {
                let duration = getValueAtPath(activities[activityId], "Settings.Duration.Value", 120);
                setAssignmentDuration(duration / 60);
                setDurationCallback(duration, activityId, week, index);
            }
            setAssignmentDurationSaveState([AssignmentSaveState.Assigned]);
        }

        refreshParentCallback();

    }, [activities, defaultAssignment, index, originalAssignment, originalDuration, refreshParentCallback, selectedActivityId, week]);

    const changeActivityDurationCallback = useCallback(
        (duration: number) => {
            if (originalAssignment !== null && selectedActivityId === originalAssignment.Id) {
                if (duration !== (originalDuration / 60)) {
                    setAssignmentDurationSaveState([AssignmentSaveState.Unsaved]);
                }
                else {
                    setAssignmentDurationSaveState([AssignmentSaveState.Assigned]);
                }
            }
            else {
                setAssignmentDurationSaveState([AssignmentSaveState.Unsaved]);
            }
            setAssignmentDuration(duration);
            setDurationCallback(duration * 60, selectedActivityId, week, index);
        }, [index, originalAssignment, originalDuration, selectedActivityId, setDurationCallback, week]
    );

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            if (activities && originalAssignment) {
                if (activities[originalAssignment.Id].hasOptions) {
                    let options = [];
                    activities[originalAssignment.Id].optionLabels.forEach(
                        (label: string, index: number) => {
                            const value: boolean = originalAssignment.Settings?.[label] ?? false;
                            options.push({
                                label: label,
                                description: activities[originalAssignment.Id].optionDescriptions[index],
                                value: value,
                                onChange: setOptionChanged
                            });
                        }
                    );
                    setAssignmentSettings(options);
                }
                else {
                    setAssignmentSettings(null);
                }
            }
        }, [activities, originalAssignment]
    );

    useEffect(
        () => {
            setSelectedActivityId(getDefaultActivityId());
            // Reset save states when data is reloaded
            setAssignmentSaveState([AssignmentSaveState.Assigned]);
            setAssignmentDurationSaveState([AssignmentSaveState.Assigned]);
            setAssignmentSettingsSaveState([AssignmentSaveState.Assigned]);
            setIsInitialized(false);
        }, [defaultAssignment, week]
    );

    useEffect(
        () => {
            if (activityOptions) {
                let filteredOptions: any[] = activityOptions;

                if (doneFilter) {
                    filteredOptions = filteredOptions.filter((e) => e.completions === 0);
                }

                if (coachingFilter || meditationFilter || movementFilter) {
                    filteredOptions = filteredOptions.filter(
                        (option) => {
                            if (option.actType === undefined) {
                                return true;
                            }

                            switch (option.actType) {
                                case "Coaching":
                                    if (coachingFilter) {
                                        return true;
                                    }
                                    break;
                                case "Movement":
                                    if (movementFilter) {
                                        return true;
                                    }
                                    break;
                                case "Meditation":
                                    if (meditationFilter) {
                                        return true;
                                    }
                                    break;
                            }

                            return false;
                        }
                    );
                }
                if (isRoutineActivity && filteredOptions.length === 0) {
                    activityOptions.forEach(o => {
                        if (o.label === activities[selectedActivityId].name) {
                            filteredOptions = [o];
                        }
                    });
                }

                if (debugState) {
                    console.info(`[${debugKey}].useEffect[coachingFilter, doneFilter, meditationFilter, movementFilter, actOptions] Regenerating selection options (setActOptionsFiltered) for activity CustomDropDowns!`);
                }

                filteredOptions = filteredOptions.sort((a, b) => (a.label || "").localeCompare(b.label || ""));

                setFilteredActivityOptions(filteredOptions);
            }
        },
        [activityOptions, coachingFilter, doneFilter, meditationFilter, movementFilter]
    );

    useEffect(
        () => {
            if (activities && !activityOptions) {
                let filteredOptions: any[] = [];

                if (!isRoutineActivity) {
                    filteredOptions.push({
                        actType: undefined,
                        completions: 1,
                        id: index,
                        img: noActivity.img,
                        label: noActivity.label,
                        value: noActivity.value
                    });
                }

                const { semver: vetKitSemver = null } = parseSemverString(vetKitVersion);

                for (const activityId in activities) {
                    if (isRoutineActivity && activities[activityId].type !== "Movement") {
                        // Only Movement activities are available in Personal Routine assignments
                        continue;
                    }
                    if (isCalibrationActivity && (activityId !== "CalibrationUpperExtremity" && activityId !== "FlowerGarden" && activityId !== "NeckCalibration")) {
                        // Not a valid Calibration activity for assignments
                        continue;
                    }
                    if (activityId === "AugmentationLumbarSpinePro" || activityId === "DogFetchPro") {
                        // Not a valid activity for assignments
                        continue;
                    }

                    let minVersion = activities[activityId].min_version;
                    const totalCompletions = activities[activityId].completions.personal + activities[activityId].completions.bonus;

                    const { semver: activityMinVersionSemver = null } = parseSemverString(minVersion);

                    const minVersionCompatible: boolean
                        = (
                            !activityMinVersionSemver || (
                                activityMinVersionSemver.major === 0 &&
                                activityMinVersionSemver.minor === 0 &&
                                activityMinVersionSemver.patch === 0
                            )
                        )
                        || (
                            vetKitSemver && satisfiesMinVersion(vetKitSemver, activityMinVersionSemver)
                        );

                    const pill: {} = minVersionCompatible
                        ? {
                            color: activities[activityId].assigned ? (totalCompletions > 0 ? 0 : 1) : 2,
                            img: activities[activityId].assigned ? (totalCompletions > 0 ? "/icons/check-circle-fancy.svg" : "/icons/check-circle-broken.svg") : "/icons/slash-circle-01.svg",
                            value: totalCompletions
                        }
                        : {
                            color: 2,
                            img: "/icons/warning.svg",
                            value: `v${minVersion}`
                        };

                    filteredOptions.push({
                        actType: activities[activityId].type,
                        completions: totalCompletions,
                        disabled: !minVersionCompatible,
                        img: `/icons/badges/ActivityIcon_${activities[activityId].type}${minVersionCompatible ? "" : "_Disabled"}.png`,
                        label: activities[activityId].name,
                        pill: pill,
                        value: activityId
                    });
                }

                if (debugState) {
                    console.info(`[${debugKey}].useEffect[activities] Regenerating activity options (setActOptions)!`);
                }

                setActivityOptions(filteredOptions);
            }
        },
        [activities, vetKitVersion]
    );

    useEffect(
        () => {
            if (optionChanged) {
                changeOption(optionChanged.label, optionChanged.value, optionChanged.options);
                setOptionChanged(null);
            }
        },
        [optionChanged]
    );

    useEffect(
        () => {
            if (selectedActivityId !== null) {
                setIsInitialized(true);
            }

            if (activities && isActivitySelected()) {
                if (getValueAtPath(activities[selectedActivityId], "Settings.DurationIncrement.Value", -1) !== -1) {
                    let minDuration: number = getValueAtPath(activities[selectedActivityId], "Settings.Duration.Value", 120) / 60;
                    let maxDuration: number = minDuration * 3;

                    let options: number[] = [minDuration];
                    for (let i: number = minDuration + 1; i <= maxDuration; i++) {
                        options.push(i);
                    }

                    setDurationOptions(
                        options.map(
                            (value: number) => (
                                {
                                    label: `${value} minute${value === 1 ? "" : "s"}`,
                                    value: value
                                }
                            )
                        )
                    );
                }
                else {
                    setDurationOptions(null);
                }
            }
        },
        [selectedActivityId, activities]
    );

    useEffect(() => {
        // Only run duplicate detection after component is fully initialized
        if (!isInitialized) {
            return;
        }

        if (isActivitySelected()) {
            if (assignmentSaveState.length === 1) {
                let foundDuplicate = false;
                currentAssignments.forEach(
                    (assignment, i: number) => {
                        if (i !== index) {
                            if (assignment !== null && assignment.Id === selectedActivityId) {
                                foundDuplicate = true;
                            }
                        }
                    }
                );

                if (foundDuplicate) {
                    if (assignmentSaveState[0] === AssignmentSaveState.Assigned) {
                        setAssignmentSaveState([AssignmentSaveState.Assigned, AssignmentSaveState.Duplicate]);
                    }
                    else if (assignmentSaveState[0] === AssignmentSaveState.Unsaved) {
                        setAssignmentSaveState([AssignmentSaveState.Unsaved, AssignmentSaveState.Duplicate]);
                    }
                }
            }
            else if (assignmentSaveState.length > 1) {
                // Check if duplicate still exists
                let foundDuplicate = false;
                currentAssignments.forEach(
                    (assignment, i: number) => {
                        if (i !== index) {
                            if (assignment !== null && assignment.Id === selectedActivityId) {
                                foundDuplicate = true;
                            }
                        }
                    }
                );

                // Only keep duplicate state if still duplicate
                if (!foundDuplicate) {
                    setAssignmentSaveState([assignmentSaveState[0]]);
                }
            }
        }
    }, [currentAssignments, originalAssignment, refreshTrigger, selectedActivityId, index, isInitialized]);

    // ---=== Render ===--- //

    const getActivityBadgeLevel = (rank: number) => {
        if (rank === 1) {return "Bronze";}
        if (rank === 2) {return "Silver";}
        if (rank >= 3) {return "Gold";}
    };

    const getActivityBadge = (activityType: string, rank: number) => {
        if (!activityType) {
            return null;
        }

        if (rank > 0) {
            let rankString = getActivityBadgeLevel(rank);
            return `/icons/badges/${activityType}Activity_${rankString}.png`;
        }
        else {
            return `/icons/badges/${activityType}Activity_None.png`;
        }
    };

    const getActivityTypeIcon = (activityType: string) => {
        let path = `/icons/badges/ActivityIcon_${activityType ? activityType : "NoAssignment"}.png`;
        return (
            <div className="flex-shrink-0 flex has-tooltip">
                <span className="tooltip rounded text-wrap shadow-lg p-1 ml-4  -mt-6 inline-flex ">{activityType ? `${activityType} Activity` : "No Assignment"}</span>
                <img className="w-7 h-7 left-0 top-0 hs-tooltip" src={path} alt="icon"/>
            </div>
        );
    };

    const getBorderColor = (state: AssignmentSaveState[]) => {
        if (state.includes(AssignmentSaveState.Duplicate)) return "border-red-500";
        if (state.includes(AssignmentSaveState.Unsaved)) return "border-yellow-500";
        return "border-gray-300";
    };

    const render = () => {
        if (!selectedActivityId) {
            return null;
        }

        const isEditable = state === ViewState.Active || state === ViewState.Pending;

        const title = isRoutineActivity
            ? (isCalibrationActivity ? "Calibration Activity" : `Movement Activity ${index / 2}`)
            : `Bonus Activity ${index + 1}`;

        let activity;
        let bestWeeklyScore = 0;
        let totalWeeklyCompletions = 0;

        if (selectedActivityId !== noActivityId) {
            if (isEditable) {
                activity = activities?.[selectedActivityId];
            }
            else if (isRoutineActivity) {
                const routineActivityKey = completedRoutineData?.[week]?.scores[index] ? Object.keys(completedRoutineData[week].scores[index])[0] : null;
                activity = routineActivityKey ? activities[routineActivityKey] : null;
            }
            else {
                activity = activities?.[defaultAssignment?.Id];
            }

            const weekStats = activity?.weekly_stats?.[week];
            if (weekStats) {
                bestWeeklyScore = isRoutineActivity ? (weekStats.best_score?.personal ?? 0) : (weekStats.best_score?.bonus ?? 0);
                totalWeeklyCompletions = (weekStats.completions?.personal ?? 0) + (weekStats.completions?.bonus ?? 0);
            }
        }

        return (
            <div className="w-full h-full p-4 flex-col justify-start items-start gap-4 inline-flex">
                <AssignmentHeader
                    title={title}
                    assignmentSaveState={assignmentSaveState}
                    assignmentDurationSaveState={assignmentDurationSaveState}
                    assignmentSettingsSaveState={assignmentSettingsSaveState}
                    isEditable={isEditable}
                    // Callbacks
                    onReset={resetActivity}
                />
                <AssignmentControls
                    activity={activity}
                    assignmentDuration={assignmentDuration}
                    assignmentDurationSaveState={assignmentDurationSaveState}
                    assignmentSaveState={assignmentSaveState}
                    assignmentSettings={assignmentSettings}
                    assignmentSettingsSaveState={assignmentSettingsSaveState}
                    coachingFilter={coachingFilter}
                    doneFilter={doneFilter}
                    durationOptions={durationOptions}
                    filteredActivityOptions={filteredActivityOptions}
                    getActivityTypeIcon={getActivityTypeIcon}
                    getBorderColor={getBorderColor}
                    index={index}
                    isEditable={isEditable}
                    isRoutineActivity={isRoutineActivity}
                    meditationFilter={meditationFilter}
                    movementFilter={movementFilter}
                    selectedActivityId={selectedActivityId}
                    setCoachingFilter={setCoachingFilter}
                    setDoneFilter={setDoneFilter}
                    setMeditationFilter={setMeditationFilter}
                    setMovementFilter={setMovementFilter}
                    // Callbacks
                    changeActivityCallback={changeActivityCallback}
                    changeActivityDurationCallback={changeActivityDurationCallback}
                    // Debugging
                    debugState={debugState}
                />
                {
                    activity && (
                        <ActivityStats
                            activity={activity}
                            bestWeeklyScore={bestWeeklyScore}
                            totalWeeklyCompletions={totalWeeklyCompletions}
                            contextBarColor={scoreBarColor}
                            contextBadgeBackgroundColor={scoreBadgeBackgroundColor}
                            contextTextColor={scoreTextColor}
                            contextLevel={scoreEncouragement}
                            getActivityBadge={getActivityBadge}
                        />
                    )
                }
            </div>
        );
    };

    return render();
};

export default AssignmentEditor;
